import { useState } from "react";
import type { MenuCategory, MenuDraft, MenuItem } from "@glowin/schemas";
import { formatDelta } from "../format.js";
import {
  isItemDirty,
  toItemForm,
  toggleGroup,
  validateItem,
  type ItemForm
} from "../item-form.js";
import {
  CheckIcon,
  Hint,
  PlusIcon,
  TrashIcon,
  WithIcon
} from "../icons.js";
import { Modal } from "../modal.js";

/**
 * Create or edit one dish.
 *
 * `item` absent means "add". Both paths share item-form.ts, so a dish added here is
 * validated identically to one that was edited — the two used to be impossible to compare
 * because adding didn't exist at all.
 */
export function ItemEditor({
  item,
  categories,
  groups,
  onSubmit,
  onDelete,
  onCancel,
  saving,
  error
}: {
  /** Undefined = creating a new dish. */
  item?: MenuItem;
  categories: MenuCategory[];
  groups: MenuDraft["modifierGroups"];
  onSubmit: (form: ItemForm) => void;
  /** Absent while creating — there's nothing to delete yet. */
  onDelete?: () => void;
  onCancel: () => void;
  saving: boolean;
  error?: string;
}) {
  const isNew = !item;
  const [original] = useState<ItemForm>(() => toItemForm(item, categories));
  const [form, setForm] = useState<ItemForm>(original);
  const [attempted, setAttempted] = useState(false);

  const errors = validateItem(form);
  const hasErrors = Object.keys(errors).length > 0;
  const dirty = isItemDirty(form, original);
  const set = (p: Partial<ItemForm>) => setForm({ ...form, ...p });
  // Hold errors back until the first save attempt: a blank "Add dish" form shouldn't open
  // covered in red.
  const errFor = (k: keyof typeof errors) => (attempted ? errors[k] : undefined);

  const submit = () => {
    setAttempted(true);
    if (!hasErrors) onSubmit(form);
  };

  const noCategories = categories.length === 0;

  return (
    <Modal label={isNew ? "Add dish" : `Edit ${item.name.en}`} onClose={onCancel}>
        <h2>{isNew ? "Add dish" : "Edit dish"}</h2>

        {error ? <div className="error">{error}</div> : null}
        {noCategories ? (
          <div className="notice warn">
            Create a category first — every dish belongs to one.
          </div>
        ) : null}

        <div className="field-row">
          <label className="field">
            <span>Name (English)</span>
            <input
              value={form.en}
              onChange={(e) => set({ en: e.target.value })}
              aria-invalid={Boolean(errFor("en"))}
              placeholder="Kung Pao Chicken"
              autoFocus
            />
            {errFor("en") ? <em className="field-error">{errFor("en")}</em> : null}
          </label>
          <label className="field">
            <span>名称 (Chinese)</span>
            <input
              value={form.zh}
              onChange={(e) => set({ zh: e.target.value })}
              aria-invalid={Boolean(errFor("zh"))}
              placeholder="optional"
            />
            {errFor("zh") ? <em className="field-error">{errFor("zh")}</em> : null}
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span>Price</span>
            <div className="price-input">
              <span className="prefix">$</span>
              <input
                value={form.priceText}
                onChange={(e) => set({ priceText: e.target.value })}
                inputMode="decimal"
                aria-invalid={Boolean(errFor("priceText"))}
                placeholder="18.99"
              />
            </div>
            {errFor("priceText") ? <em className="field-error">{errFor("priceText")}</em> : null}
          </label>
          <label className="field">
            <span>Category</span>
            <select
              value={form.categoryId}
              onChange={(e) => set({ categoryId: e.target.value })}
              aria-invalid={Boolean(errFor("categoryId"))}
              disabled={noCategories}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name.en}
                </option>
              ))}
            </select>
            {errFor("categoryId") ? <em className="field-error">{errFor("categoryId")}</em> : null}
          </label>
        </div>

        <label className="field">
          <span>Description</span>
          <input
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
            aria-invalid={Boolean(errFor("description"))}
            placeholder="Helps the phone agent answer “what’s in it?”"
          />
          {errFor("description") ? (
            <em className="field-error">{errFor("description")}</em>
          ) : null}
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.available}
            onChange={(e) => set({ available: e.target.checked })}
          />
          <span>
            Available <Hint>Sold-out dishes are hidden from the phone agent.</Hint>
          </span>
        </label>

        <fieldset className="groups-picker">
          <legend>
            Options
            <Hint>
              The agent asks about these. Option groups are shared, so editing one changes every dish that
              uses it.
            </Hint>
          </legend>
          {groups.length === 0 ? (
            <p className="muted">No option groups yet.</p>
          ) : (
            groups.map((g) => {
              const checked = form.groupIds.includes(g.id);
              return (
                <label key={g.id} className={`group-option${checked ? " on" : ""}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setForm(toggleGroup(form, g.id))}
                  />
                  <span className="group-option-body">
                    <strong>{g.name.en}</strong>{" "}
                    <span className="muted">
                      {g.required ? "required" : "optional"} · pick {g.minSelect}–{g.maxSelect}
                    </span>
                    <span className="opts">
                      {g.options.map((o) => (
                        <span key={o.id} className="opt">
                          {o.name.en} <em>{formatDelta(o.priceDelta)}</em>
                        </span>
                      ))}
                    </span>
                  </span>
                </label>
              );
            })
          )}
        </fieldset>

        <div className="modal-actions">
          {onDelete ? (
            <button
              className="danger left"
              onClick={onDelete}
              disabled={saving}
              title="Remove this dish from the menu"
            >
              <WithIcon icon={<TrashIcon size={16} />}>Delete</WithIcon>
            </button>
          ) : null}
          <button className="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            className="primary"
            onClick={submit}
            disabled={saving || noCategories || (!isNew && !dirty)}
          >
            <WithIcon icon={isNew ? <PlusIcon size={16} /> : <CheckIcon size={16} />}>
              {saving ? "Saving…" : isNew ? "Add dish" : "Save"}
            </WithIcon>
          </button>
        </div>
    </Modal>
  );
}
